import { z } from "zod";
import {
  FriendListEntry,
  PaginationQuery,
  ProfileId,
  ProfileSearchQuery,
  ProfileSummary,
  Relationship,
} from "../../common";

const EXAMPLE = {
  profileId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
  otherProfileId: "0199f0c1-7e2a-7c9d-a4f3-2b6e8d1c5a70",
  since: "2026-09-10T14:32:00.000Z",
  cursor: "2026-09-10T14:32:00.000Z,0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
} as const;

const exampleProfile = {
  profileId: EXAMPLE.otherProfileId,
  username: "Grace_Hopper",
  displayName: "Grace Hopper",
  avatar: null,
} as const;

const exampleEntry = { profile: exampleProfile, since: EXAMPLE.since } as const;

// Route parameters

export const FriendProfileIdParams = z
  .strictObject({
    profileId: ProfileId.meta({
      description: "The other person's profile id.",
      example: EXAMPLE.otherProfileId,
    }),
  })
  .meta({
    id: "FriendProfileIdParams",
    description: "A route parameter naming the other profile in a friendship or request.",
    example: { profileId: EXAMPLE.otherProfileId },
  });
export type FriendProfileIdParams = z.infer<typeof FriendProfileIdParams>;

// Summary

export const FriendSummaryResponse = z
  .strictObject({
    friendCount: z.number().int().nonnegative().meta({
      description: "Accepted friendships the caller is part of.",
      example: 12,
    }),
    receivedRequestCount: z.number().int().nonnegative().meta({
      description: "Pending requests waiting on the caller. Drive the badge from this.",
      example: 2,
    }),
    sentRequestCount: z.number().int().nonnegative().meta({
      description: "Pending requests the caller has sent and may still cancel.",
      example: 1,
    }),
  })
  .meta({
    id: "FriendSummaryResponse",
    description: "The caller's own friend and request counts.",
    example: { friendCount: 12, receivedRequestCount: 2, sentRequestCount: 1 },
  });
export type FriendSummaryResponse = z.infer<typeof FriendSummaryResponse>;

// Lists

export const FriendListTab = z.enum(["friends", "received", "sent"]).meta({
  id: "FriendListTab",
  description:
    "`friends`: accepted friendships. `received`: pending requests the caller may accept or decline. `sent`: pending requests the caller may cancel.",
  example: "friends",
});
export type FriendListTab = z.infer<typeof FriendListTab>;

export const ListFriendsQuery = PaginationQuery.extend({
  cursor: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3,6}Z,[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    )
    .optional(),
  tab: FriendListTab.default("friends"),
}).meta({
  id: "ListFriendsQuery",
  description:
    "Which list to page through. Pages are newest first by `since`, then by profile id; pass `nextCursor` back as `cursor`.",
  example: { tab: "friends", limit: 20 },
});
export type ListFriendsQuery = z.infer<typeof ListFriendsQuery>;

export const ListFriendsResponse = z
  .strictObject({
    items: z.array(FriendListEntry),
    nextCursor: z.string().nullable().meta({
      description: "Send as `cursor` for the next page, or null on the last page.",
      example: EXAMPLE.cursor,
    }),
  })
  .meta({
    id: "ListFriendsResponse",
    description: "One page of the requested list.",
    example: { items: [exampleEntry], nextCursor: null },
  });
export type ListFriendsResponse = z.infer<typeof ListFriendsResponse>;

// Mutations

export const SendFriendRequestBody = z
  .strictObject({
    profileId: ProfileId.meta({
      description:
        "The profile to send a request to. Resolve a username to its id with search first.",
      example: EXAMPLE.otherProfileId,
    }),
  })
  .meta({
    id: "SendFriendRequestBody",
    description: "Who to send a friend request to.",
    example: { profileId: EXAMPLE.otherProfileId },
  });
export type SendFriendRequestBody = z.infer<typeof SendFriendRequestBody>;

export const SendFriendRequestResponse = z
  .strictObject({
    outcome: z.enum(["requested", "already_pending", "incoming_exists", "already_friends"]).meta({
      description:
        "`requested`: a new pending request was created. `already_pending`: the caller had already sent one. `incoming_exists`: the other person already sent the caller a request, so accept that instead; nothing was written. `already_friends`: the two are already friends.",
      example: "requested",
    }),
  })
  .meta({
    id: "SendFriendRequestResponse",
    description:
      "What the request did. The outcome names the caller's new relationship: `requested` and `already_pending` leave it `outgoing`, `incoming_exists` means `incoming`, and `already_friends` means `friends`.",
    example: { outcome: "requested" },
  });
export type SendFriendRequestResponse = z.infer<typeof SendFriendRequestResponse>;

// Relationship and mutuals

export const FriendRelationshipResponse = z
  .strictObject({
    relationship: Relationship,
    mutualFriendCount: z.number().int().nonnegative().meta({
      description:
        "Friends the caller and this profile have in common. 0 when the profile is the caller's own.",
      example: 3,
    }),
  })
  .meta({
    id: "FriendRelationshipResponse",
    description: "Where the caller stands with one profile, for a profile page's friend control.",
    example: { relationship: "none", mutualFriendCount: 3 },
  });
export type FriendRelationshipResponse = z.infer<typeof FriendRelationshipResponse>;

export const ListMutualFriendsQuery = PaginationQuery.extend({
  cursor: ProfileId.optional(),
}).meta({
  id: "ListMutualFriendsQuery",
  description: "Pages are ordered by profile id; pass `nextCursor` back as `cursor`.",
  example: { limit: 20 },
});
export type ListMutualFriendsQuery = z.infer<typeof ListMutualFriendsQuery>;

export const ListMutualFriendsResponse = z
  .strictObject({
    profiles: z.array(ProfileSummary).meta({
      description: "Profiles that are friends with both the caller and the named profile.",
    }),
    total: z.number().int().nonnegative().meta({
      description: "How many mutual friends exist across all pages.",
      example: 3,
    }),
    nextCursor: z.string().nullable().meta({
      description: "Send as `cursor` for the next page, or null on the last page.",
      example: null,
    }),
  })
  .meta({
    id: "ListMutualFriendsResponse",
    description: "One page of mutual friends and the total.",
    example: { profiles: [exampleProfile], total: 3, nextCursor: null },
  });
export type ListMutualFriendsResponse = z.infer<typeof ListMutualFriendsResponse>;

// Search

export const FriendSearchQuery = ProfileSearchQuery.meta({
  id: "FriendSearchQuery",
  description: "A small autocomplete search by profile id or username prefix.",
  example: { q: "gra", limit: 10 },
});
export type FriendSearchQuery = z.infer<typeof FriendSearchQuery>;

export const SearchFriendsResponse = z
  .strictObject({
    results: z.array(FriendListEntry).meta({
      description:
        "The caller's friends whose username starts with the query, or the one friend with that id. Ordered by username, then profile id.",
    }),
  })
  .meta({
    id: "SearchFriendsResponse",
    description: "Friends matching the query. Every result is already a friend.",
    example: { results: [exampleEntry] },
  });
export type SearchFriendsResponse = z.infer<typeof SearchFriendsResponse>;

export const ProfileSearchResult = z
  .strictObject({
    profile: ProfileSummary,
    relationship: Relationship,
  })
  .meta({
    id: "ProfileSearchResult",
    description: "One matching profile and where the caller stands with it.",
    example: { profile: exampleProfile, relationship: "none" },
  });
export type ProfileSearchResult = z.infer<typeof ProfileSearchResult>;

export const SearchProfilesResponse = z
  .strictObject({
    results: z.array(ProfileSearchResult).meta({
      description:
        "Connected profiles whose username starts with the query, or the one profile with that id, each with the caller's relationship. Ordered by username, then profile id.",
    }),
  })
  .meta({
    id: "SearchProfilesResponse",
    description: "Profiles matching the query, each tagged with the caller's relationship.",
    example: { results: [{ profile: exampleProfile, relationship: "none" }] },
  });
export type SearchProfilesResponse = z.infer<typeof SearchProfilesResponse>;
